import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { ImageIcon } from "Components/DesignSystem/Iconography/Icons";
import Checkbox from "Components/DesignSystem/Checkbox/Checkbox";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";

import { AppContext } from "Context/AppContext";
import Paginator from "Components/Common/PaginatorWithApi";
import {
  exportProductSyncDiagnostics,
  getProductSyncDiagnostics,
  TDiagnosticsEnvColumn,
  TDiagnosticsProduct,
  TDiagnosticsResponse,
} from "api/Diagnostics";
import {
  fetchEnvironments,
  fetchParentChannels,
} from "Components/Admin/connections/Services/syncEnvironmentsService";
import { PRODUCT_DELTA_LIST } from "Components/GMS/Products/ProductConstants";
import SyncProductsModal from "./SyncProductsModal";

const ProductThumbnail = ({ src, alt }: { src?: string | null; alt: string }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span
        role="img"
        aria-label={`No image for ${alt}`}
        className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-surface-sunken tw-text-ink-tertiary"
      >
        <ImageIcon size={16} aria-hidden="true" />
      </span>
    );
  }
  return <img src={src} alt={alt} onError={() => setFailed(true)} className="tw-h-full tw-w-full tw-object-contain" />;
};

/* ------------------------------------------------------------------ */
/* Icons (kept local to match the Figma spec exactly — US 12396)       */
/* ------------------------------------------------------------------ */

const BackChevron = () => (
  <svg width="9" height="16" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 1L1 8L8 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SyncGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 8a6 6 0 0 1 10.24-4.24M14 8a6 6 0 0 1-10.24 4.24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.5 1v2.6h-2.6M3.5 15v-2.6h2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 1.5v8.5m0 0L4.75 6.75M8 10l3.25-3.25M2 12.5v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoGlyph = ({ color = "currentColor" }: { color?: string }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="9" r="7.25" stroke={color} strokeWidth="1.3" />
    <path d="M9 8v4" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="9" cy="5.6" r="0.9" fill={color} />
  </svg>
);

/** Up/down caret pair; the arrow matching `dir` is highlighted. */
const SortCaret = ({ dir }: { dir?: "asc" | "desc" }) => (
  <svg className="tw-inline-block tw-ml-1" width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0.5L7 4H1L4 0.5Z" fill={dir === "asc" ? "#0065FF" : "#97A0AF"} />
    <path d="M4 11.5L1 8H7L4 11.5Z" fill={dir === "desc" ? "#0065FF" : "#97A0AF"} />
  </svg>
);

/** green filled circle + white tick = SKU-mapped */
const SyncedCell = () => (
  <span className="tw-inline-flex tw-items-center tw-justify-center" aria-label="Synced">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="10" fill="#36B37E" />
      <path d="M6 10.2l2.6 2.6L14 7.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

/** red filled circle + white cross = sync gap */
const GapCell = () => (
  <span className="tw-inline-flex tw-items-center tw-justify-center" aria-label="Sync gap">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="10" fill="#FF5630" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  </span>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const DEFAULT_LIMIT = 10;
const CHECKBOX_COL_WIDTH = 48;
const PRODUCT_COL_WIDTH = 400;
const ENV_COL_WIDTH = 160;

const Diagnostics = (props: any) => {
  const navigate = useNavigate();
  const params = useParams<{ categoryid?: string }>();
  const appData = useContext(AppContext);
  const categoryid: string = params?.categoryid ?? "";

  const [data, setData] = useState<TDiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [syncModalOpen, setSyncModalOpen] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [download, setDownload] = useState<{ url: string; nonce: number } | null>(null);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  // default sort: most sync gaps first. The backend sorts on the gap count only,
  // so this is a direction toggle — there is no per-column sort.
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Channel-environment columns for the current domain. The backend does not
  // enumerate them; it evaluates each product against the `channel_envs` we send,
  // so this list is both the column set and the request parameter.
  const [columns, setColumns] = useState<TDiagnosticsEnvColumn[]>([]);
  const [columnsLoading, setColumnsLoading] = useState<boolean>(true);

  const partyId: string = appData?.selectedParty?.id ?? "";
  const domainId: string = appData?.selectedIndustry?.id ?? "";
  const currentChannelKey: string = appData?.channelKey ?? "";

  // Workspace=party, Domain=industry, Channel=parent channel, Environment=child
  // channel. Every child channel_key under the domain's parent channels is one
  // column, i.e. one SKU-mapping target.
  useEffect(() => {
    let cancelled = false;
    if (!partyId || !domainId) {
      setColumnsLoading(false);
      return;
    }
    setColumnsLoading(true);

    (async () => {
      const channelsRes = await fetchParentChannels(partyId, domainId);
      if (!channelsRes.success || !channelsRes.data?.length) {
        if (!cancelled) {
          setColumns([]);
          setColumnsLoading(false);
          toast.error(channelsRes.error ?? "Failed to load channels for this domain");
        }
        return;
      }

      const perChannel = await Promise.all(
        channelsRes.data.map(async (channel) => {
          const envRes = await fetchEnvironments(partyId, channel.id);
          return (envRes.data ?? []).map((env) => ({
            channel_key: env.key,
            channel_name: channel.name,
            environment_name: env.name,
            label: `${channel.name} - ${env.name}`,
            is_current: env.key === currentChannelKey,
          }));
        })
      );

      if (cancelled) return;
      // current environment column always rendered first
      const cols = perChannel
        .flat()
        .sort((a, b) => Number(b.is_current) - Number(a.is_current));
      setColumns(cols);
      setColumnsLoading(false);
      if (!cols.length) toast.error("No environments found for this domain");
    })();

    return () => {
      cancelled = true;
    };
  }, [partyId, domainId, currentChannelKey]);

  const channelEnvs: string[] = useMemo(
    () => columns.map((c) => c.channel_key),
    [columns]
  );

  const products: TDiagnosticsProduct[] = data?.products ?? [];
  const totalCount = data?.total_count ?? 0;

  // Data loads automatically on open and on each pagination / sort change.
  const fetchData = useCallback(() => {
    if (!categoryid || columnsLoading) return;
    if (!channelEnvs.length) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getProductSyncDiagnostics(categoryid, channelEnvs, { page, limit, sort_order: sortOrder })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          toast.error(err?.message ?? "Failed to load diagnostics");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryid, channelEnvs, columnsLoading, page, limit, sortOrder]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  const goBack = () => navigate(`${PRODUCT_DELTA_LIST}/${categoryid}`);

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const pageIds: string[] = products.map((product) => product.gms_id);
  const pageSelectedCount = pageIds.filter((id) => selectedIds.includes(id)).length;
  const allPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;

  const togglePage = (checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? Array.from(new Set([...prev, ...pageIds])) : prev.filter((id) => !pageIds.includes(id))
    );
  };

  // Only the sync-gap count is sortable — the backend takes a direction, not a
  // sort key. Per-column sorting (US 12396, "optional") needs backend support.
  const toggleGapSort = () => {
    setSortOrder((d) => (d === "desc" ? "asc" : "desc"));
    setPage(1);
  };

  const hasSelection = selectedIds.length > 0;
  const hasProductRows = !columnsLoading && !loading && products.length > 0;

  const selectedProducts = useMemo(
    () => products.filter((product) => selectedIds.includes(product.gms_id)),
    [products, selectedIds]
  );

  const handleSync = () => setSyncModalOpen(true);

  // After a sync the mappings exist, so the synced products drop out of the
  // gap list — clear the selection and reload from page 1.
  const handleSynced = () => {
    setSelectedIds([]);
    setPage(1);
    fetchData();
  };

  // "Domain" in the top ribbon maps to the selected industry (Workspace=party,
  // Domain=industry, Channel=parent channel, Environment=child channel). The
  // backend response carries no domain name, so it comes from context.
  const domainName = appData?.selectedIndustry?.name ?? "this";

  const canExport = !columnsLoading && columns.length > 0 && !!categoryid;

  const handleDownloadExcel = async () => {
    if (!canExport || exporting) return;
    setExporting(true);
    try {
      const res = await exportProductSyncDiagnostics(categoryid, {
        columns: columns.map(({ channel_key, label, is_current }) => ({ channel_key, label, is_current })),
        domain_name: appData?.selectedIndustry?.name ?? "",
        sort_order: sortOrder,
      });
      setDownload({ url: res.url, nonce: Date.now() });
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to download diagnostics");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div id="diagnostics-page" className="tw-max-w-full">
      {/* Header */}
      <div
        id="diagnostics-header"
        data-testid="diagnostics-header"
        className="tw-sticky -tw-top-3 tw-z-10 -tw-mt-3 tw-flex tw-items-center tw-justify-between tw-bg-surface tw-pb-3 tw-pt-6"
      >
        <div className="tw-flex tw-items-center tw-gap-3">
          <button id="diagnostics-btn-back" onClick={goBack} className="tw-flex tw-items-center" aria-label="Back to products">
            <BackChevron />
          </button>
          <h1 className="tw-text-xl tw-font-semibold tw-text-ink tw-m-0">Diagnostics</h1>
        </div>
        <div className="tw-flex tw-items-center tw-gap-6">
          <button
            id="diagnostics-btn-sync"
            onClick={handleSync}
            disabled={!hasSelection}
            className={`tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium ${
              hasSelection ? "tw-text-accent tw-cursor-pointer" : "tw-text-ink-disabled tw-cursor-not-allowed"
            }`}
          >
            <SyncGlyph />
            Sync Products
          </button>
          <button
            id="diagnostics-btn-download"
            data-testid="diagnostics-btn-download"
            type="button"
            onClick={handleDownloadExcel}
            disabled={!canExport || exporting}
            aria-busy={exporting}
            className={`tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium ${
              canExport && !exporting ? "tw-text-ink tw-cursor-pointer" : "tw-text-ink-disabled tw-cursor-not-allowed"
            }`}
          >
            <DownloadGlyph />
            {exporting ? "Downloading..." : "Download Excel"}
          </button>
          {download && (
            <iframe
              key={download.nonce}
              src={download.url}
              title="diagnostics-download"
              data-testid="diagnostics-download-frame"
              style={{ display: "none" }}
            />
          )}
        </div>
      </div>

      {/* Info bar 1 */}
      <div id="diagnostics-info-scope" className="tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-accent tw-bg-accent-tint tw-px-4 tw-py-2.5 tw-my-2">
        <span className="tw-text-accent tw-flex"><InfoGlyph /></span>
        <span className="tw-text-sm tw-text-ink">
          Showing only products <strong>not mapped to all channels and environments</strong> in the{" "}
          <strong>{domainName}</strong> domain. Sorted by number of sync gaps,{" "}
          {sortOrder === "desc" ? "highest first" : "lowest first"}.
        </span>
      </div>

      {/* Info bar 2 — count across ALL pages */}
      <div
        id="diagnostics-info-count"
        className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded tw-bg-surface-sunken tw-px-4 tw-py-2.5 tw-mb-3"
      >
        <span className="tw-text-sm tw-text-ink-secondary">
          <strong>{totalCount} product{totalCount === 1 ? "" : "s"}</strong> have sync gaps in this category. Select
          one or more and use <strong>Sync</strong> to push them to other environments.
        </span>
        <button
          id="diagnostics-btn-sort-gaps"
          onClick={toggleGapSort}
          className="tw-flex tw-shrink-0 tw-items-center tw-text-sm tw-font-medium tw-text-ink-secondary tw-cursor-pointer tw-whitespace-nowrap"
          aria-label={`Sort by sync gaps, ${sortOrder === "desc" ? "highest" : "lowest"} first`}
        >
          Sync gaps
          <SortCaret dir={sortOrder} />
        </button>
      </div>

      {/* Table */}
      <div id="diagnostics-table-container" className="tw-overflow-x-auto tw-border-x tw-border-edge tw-rounded-md">
        <table
          className="tw-border-separate tw-text-sm"
          style={{
            tableLayout: "fixed",
            borderSpacing: 0,
            width: CHECKBOX_COL_WIDTH + PRODUCT_COL_WIDTH + columns.length * ENV_COL_WIDTH,
            minWidth: "100%",
          }}
          data-testid="diagnostics-table"
        >
          <colgroup>
            <col style={{ width: CHECKBOX_COL_WIDTH }} />
            <col style={{ width: PRODUCT_COL_WIDTH }} />
            {columns.map((col) => (
              <col key={col.channel_key} style={{ width: ENV_COL_WIDTH }} />
            ))}
          </colgroup>
          <thead>
            <tr className="tw-text-left tw-text-ink-secondary">
              <th
                className="tw-sticky tw-z-[2] tw-bg-surface-sunken tw-border-t tw-border-edge tw-px-4 tw-py-3"
                style={{ left: 0 }}
              >
                <Checkbox
                  size="sm"
                  checked={allPageSelected}
                  indeterminate={pageSelectedCount > 0 && !allPageSelected}
                  disabled={!pageIds.length}
                  onChange={togglePage}
                  aria-label="Select all products on this page"
                  data-testid="diagnostics-select-all"
                />
              </th>
              <th
                className="tw-sticky tw-z-[2] tw-bg-surface-sunken tw-border-t tw-border-r tw-border-edge tw-px-2 tw-py-3 tw-font-semibold tw-whitespace-nowrap"
                style={{ left: CHECKBOX_COL_WIDTH }}
                data-testid="diagnostics-col-product"
              >
                Product
              </th>
              {columns.map((col) => {
                const envLabel = `${col.environment_name}${col.is_current ? " (Current)" : ""}`;
                return (
                  <th
                    key={col.channel_key}
                    className={`tw-overflow-hidden tw-px-3 tw-py-2 tw-text-center tw-font-semibold ${
                      col.is_current
                        ? `tw-bg-accent-tint tw-border-x tw-border-t tw-border-accent tw-text-accent${hasProductRows ? "" : " tw-border-b"}`
                        : "tw-bg-surface-sunken tw-border-t tw-border-edge"
                    }`}
                    data-testid={`diagnostics-col-${col.channel_key}`}
                  >
                    <Tooltip content={`${col.channel_name} - ${envLabel}`} placement="top" usePortal block>
                      <span className="tw-flex tw-flex-col tw-items-center tw-leading-tight">
                        <span className="tw-block tw-max-w-full tw-truncate" data-testid={`diagnostics-col-${col.channel_key}-channel`}>
                          {col.channel_name}
                        </span>
                        <span className="tw-block tw-max-w-full tw-truncate tw-text-xs tw-font-normal" data-testid={`diagnostics-col-${col.channel_key}-env`}>
                          {envLabel}
                        </span>
                      </span>
                    </Tooltip>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {columnsLoading || loading ? (
              <tr>
                <td colSpan={columns.length + 2} className="tw-border-b tw-border-edge tw-px-4 tw-py-10 tw-text-center tw-text-ink-tertiary">
                  Loading diagnostics…
                </td>
              </tr>
            ) : !columns.length ? (
              <tr>
                <td colSpan={2} className="tw-border-b tw-border-edge tw-px-4 tw-py-10 tw-text-center tw-text-ink-tertiary">
                  No channels or environments are configured for this domain.
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} className="tw-border-b tw-border-edge tw-px-4 tw-py-10 tw-text-center tw-text-ink-tertiary">
                  No sync gaps found — all products are mapped across every channel and environment.
                </td>
              </tr>
            ) : (
              products.map((product, rowIndex) => {
                const lastRowEdge = rowIndex === products.length - 1 ? " tw-border-b" : "";
                return (
                <tr key={product.gms_id} className="tw-group">
                  <td
                    className={`tw-sticky tw-z-[1] tw-bg-surface group-hover:tw-bg-surface-hover tw-border-t${lastRowEdge} tw-border-edge tw-px-4 tw-py-1.5`}
                    style={{ left: 0 }}
                  >
                    <Checkbox
                      size="sm"
                      checked={selectedIds.includes(product.gms_id)}
                      onChange={(v) => toggleRow(product.gms_id, v)}
                      aria-label={`Select ${product.name}`}
                      data-testid={`diagnostics-row-select-${product.gms_id}`}
                    />
                  </td>
                  <td
                    className={`tw-sticky tw-z-[1] tw-bg-surface group-hover:tw-bg-surface-hover tw-border-t tw-border-r${lastRowEdge} tw-border-edge tw-px-2 tw-py-1.5`}
                    style={{ left: CHECKBOX_COL_WIDTH }}
                  >
                    <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                      <div className="tw-h-8 tw-w-8 tw-shrink-0 tw-overflow-hidden tw-rounded tw-border tw-border-edge tw-bg-surface">
                        <ProductThumbnail src={product.image_url} alt={product.name} />
                      </div>
                      <span className="tw-truncate tw-font-medium tw-text-ink" title={product.name}>{product.name}</span>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.channel_key}
                      className={`tw-border-t tw-px-4 tw-py-1.5 tw-text-center group-hover:tw-bg-surface-hover ${
                        col.is_current
                          ? `tw-bg-accent-tint tw-border-x tw-border-accent${lastRowEdge}`
                          : `tw-border-edge${lastRowEdge}`
                      }`}
                      data-testid={`diagnostics-cell-${product.gms_id}-${col.channel_key}`}
                    >
                      {product.mapping?.[col.channel_key] ? <SyncedCell /> : <GapCell />}
                    </td>
                  ))}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-py-3">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
          <span>Show</span>
          <select
            className="tw-border tw-border-edge tw-bg-surface tw-text-ink tw-p-1"
            style={{ width: "80px" }}
            aria-label="Records on page select"
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <Paginator
          onPageChange={(p: number) => setPage(p)}
          recordPerPage={limit}
          totalRecords={totalCount || 1}
          presentPage={page}
        />
      </div>

      {syncModalOpen && (
        <SyncProductsModal
          isOpen={syncModalOpen}
          onClose={() => setSyncModalOpen(false)}
          products={selectedProducts}
          onSynced={handleSynced}
        />
      )}
    </div>
  );
};

export default Diagnostics;
